import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, CheckCircle2, Circle, FileText, Lock, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, StatusBadge, Tone } from "@/components/shared";
import { ProjectDocsDialog } from "@/components/ProjectDocs";
import { AuditList } from "@/components/AuditList";
import { GalleryButton } from "@/components/GalleryButton";
import { MediaUploader } from "@/components/Media";
import { DeliveryForm, NotifyDialog, ProjectEditDialog } from "@/components/ProjectExtras";
import { BuildSiblings, ProcureDialog, StageBom, StageTimeline, TrackingButton } from "@/components/ProjectOps";
import { useAuth } from "@/lib/auth";
import { can, stageAction } from "@/lib/roles";
import { api, errMsg, fdt, inr, label, useApi } from "@/lib/api";

function StagePanel({ project, stage, setProject }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(stage.stageNotes || "");
  const isCurrent = stage.id === project.currentStageId;
  const visitDay = project.customerVisitAt?.slice(0, 10);
  const onHold = !!visitDay && stage.sequenceOrder > 1 && new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) < visitDay;
  const active = isCurrent && can(user, stageAction(stage)) && !onHold;
  const isPhoto = stage.stageName.toLowerCase().includes("photo");
  const isDelivery = stage.stageName.toLowerCase().includes("deliver");
  const allChecked = stage.checklistsState.every((c) => c.checked);
  const photosOk = !stage.requiresPhotos || stage.proofImages.length > 0;
  const deliveryOk = !isDelivery || !!project.delivery;
  const isLast = Math.max(...project.stages.map((s) => s.sequenceOrder)) === stage.sequenceOrder;
  const gated = stage.stageName.toLowerCase().includes("procure") || stage.showProcurementChecklist || isLast;
  const procuredOk = !gated || project.parts.every((p) => p.inStock);
  const canComplete = active && allChecked && photosOk && deliveryOk && procuredOk;
  const call = async (fn, msg) => {
    try { setProject((await fn()).data); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  const base = `/projects/${project.id}/stages/${stage.id}`;
  const toggleItem = (i, checked) => {
    const patchStage = (p, list) => ({ ...p, stages: p.stages.map((s) => (s.id === stage.id ? { ...s, checklistsState: list } : s)) });
    const before = stage.checklistsState;
    setProject((p) => patchStage(p, before.map((c, j) => (j === i ? { ...c, checked, checkedBy: checked ? user?.name : null, checkedAt: checked ? new Date().toISOString() : null } : c))));
    api.patch(`${base}/checklist`, { index: i, checked }).catch((e) => {
      setProject((p) => patchStage(p, p.stages.find((s) => s.id === stage.id).checklistsState.map((c, j) => (j === i ? before[i] : c))));
      toast.error(errMsg(e));
    });
  };
  const checkedCount = stage.checklistsState.filter((c) => c.checked).length;
  return (
    <Panel title={<span className="flex items-center gap-2">{stage.stageName}{stage.requiresPhotos && <Tone tone="purple"><Camera className="h-3 w-3 mr-1" />Photo proof required</Tone>}</span>}
      action={stage.isCompleted ? <Tone tone="green">Completed {fdt(stage.completedAt)}</Tone> : isCurrent ? <Tone tone="blue">{active ? "Active stage" : "Active · view only"}</Tone> : <Tone tone="gray"><Lock className="h-3 w-3 mr-1" />Locked</Tone>}
      data-testid="stage-panel">
      <div className="p-5 space-y-6">
        {onHold && <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400" data-testid="visit-hold-banner">
          On hold — customer is visiting the store on {fdt(project.customerVisitAt)} to witness the build. This stage unlocks on that day.</div>}
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-2"><span>Checklist</span><span className="num" data-testid="checklist-progress">{checkedCount}/{stage.checklistsState.length}</span></div>
          <Progress value={(checkedCount / (stage.checklistsState.length || 1)) * 100} className="h-1 mb-3" />
          <div className="space-y-1.5">
            {stage.checklistsState.map((c, i) => (
              <label key={i} className={`flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm ${c.checked ? "bg-secondary/40" : ""} ${active ? "cursor-pointer" : ""}`}>
                <Checkbox checked={c.checked} disabled={!active} onCheckedChange={(v) => toggleItem(i, !!v)} className="mt-0.5" data-testid={`checklist-item-${i}`} />
                <div className="flex-1"><div className={c.checked ? "line-through text-muted-foreground" : ""}>{c.item}</div>
                  {c.checked && <div className="text-[11px] text-muted-foreground">{c.checkedBy} · {fdt(c.checkedAt)}</div>}</div>
              </label>
            ))}
          </div>
        </div>
        <StageTimeline project={project} stage={stage} onSaved={setProject} canEdit={can(user, stageAction(stage))} />
        <StageBom project={project} stage={stage} />
        {(stage.requiresPhotos || stage.proofImages.length > 0 || isPhoto) && (
          <div>
            <div className="text-xs text-muted-foreground mb-2">{isPhoto ? "Photoshoot media — upload images/videos or paste a link" : "Proof photos"} {stage.requiresPhotos && "(at least one required)"}</div>
            <MediaUploader items={stage.proofImages} disabled={!active} testid="proof-media"
              onAdd={(m) => call(() => api.post(`${base}/photos`, m), "Media added")} />
          </div>
        )}
        {isDelivery && <DeliveryForm project={project} onSaved={setProject} disabled={!active} />}
        <div>
          <div className="text-xs text-muted-foreground mb-2">Stage notes</div>
          <Textarea rows={2} value={notes} disabled={!active} onChange={(e) => setNotes(e.target.value)} data-testid="stage-notes-input" />
          {active && notes !== (stage.stageNotes || "") && <Button size="sm" variant="ghost" className="mt-2" onClick={() => call(() => api.patch(`${base}/notes`, { stageNotes: notes }), "Notes saved")} data-testid="save-stage-notes">Save notes</Button>}
        </div>
        {active && (
          <div className="border-t pt-5">
            {!canComplete && (
              <ul className="text-xs text-muted-foreground mb-3 space-y-1" data-testid="stage-gate-reasons">
                {!allChecked && <li>• Complete all checklist items</li>}
                {!photosOk && <li>• Upload at least one proof photo</li>}
                {!deliveryOk && <li>• Record delivery details</li>}
                {!procuredOk && <li data-testid="gate-procure-reason">• Mark all 'To be procured' parts as Procured (Parts & Serials tab)</li>}
              </ul>
            )}
            <Button disabled={!canComplete} onClick={() => call(() => api.post(`${base}/complete`), `${stage.stageName} completed`)} data-testid="complete-stage-button">
              <CheckCircle2 className="h-4 w-4 mr-2" />Complete Stage & Move to Next Step
            </Button>
          </div>
        )}
      </div>
    </Panel>
  );
}

function PartsTab({ project, setProject }) {
  const { user } = useAuth();
  const editable = can(user, "projects:parts");
  const [vals, setVals] = useState(Object.fromEntries(project.parts.map((p) => [p.id, p.serialNumber])));
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");
  const submit = async (part, n = "") => {
    try {
      setProject((await api.patch(`/projects/${project.id}/parts/${part.id}`, { serialNumber: vals[part.id], note: n })).data);
      toast.success("Serial number saved");
      setPending(null); setNote("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const save = (part) => (part.serialNumber ? (setPending(part), setNote("")) : submit(part));
  const [procuring, setProcuring] = useState(null);
  const procure = (part) => setProcuring(part);
  return (
    <div className="rounded-lg border bg-card overflow-x-auto">
      {procuring && <ProcureDialog project={project} part={procuring} onClose={() => setProcuring(null)} onSaved={setProject} />}
      <table className="w-full text-sm" data-testid="parts-table">
        <thead><tr className="border-b bg-muted/40 text-xs text-muted-foreground">{["Category", "Part", "Stock", "Serial number", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>)}</tr></thead>
        <tbody>{project.parts.map((p) => (
          <tr key={p.id} className="border-b last:border-0" data-testid={`part-row-${p.category}`}>
            <td className="px-4 py-3 text-xs text-muted-foreground w-36">{label(p.category)}</td>
            <td className="px-4 py-3"><div className="font-medium">{p.customPartName}</div>{p.serialChangeNote && <div className="text-[11px] text-amber-600 mt-0.5">Changed: {p.serialChangeNote}</div>}</td>
            <td className="px-4 py-3">{p.inStock ? <Tone tone="green" data-testid={`part-stock-${p.category}`}>In Stock</Tone> :
              <div className="flex items-center gap-2"><Tone tone="amber" data-testid={`part-stock-${p.category}`}>To be Procured</Tone>{editable && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => procure(p)} data-testid={`mark-procured-${p.category}`}>Mark procured</Button>}</div>}</td>
            <td className="px-4 py-3 w-64"><Input disabled={!editable} className="h-8 font-mono text-xs" value={vals[p.id] || ""} onChange={(e) => setVals({ ...vals, [p.id]: e.target.value })} placeholder="Enter serial" data-testid={`serial-input-${p.category}`} /></td>
            <td className="px-4 py-3 w-20">{(vals[p.id] || "") !== (p.serialNumber || "") && <Button size="sm" variant="outline" className="h-8" onClick={() => save(p)} data-testid={`save-serial-${p.category}`}>Save</Button>}</td>
          </tr>
        ))}</tbody>
      </table>
      <Dialog open={!!pending} onOpenChange={() => setPending(null)}>
        <DialogContent data-testid="serial-change-dialog">
          <DialogHeader><DialogTitle>Explain serial number change</DialogTitle>
            <DialogDescription>{pending?.customPartName}: <span className="font-mono">{pending?.serialNumber}</span> → <span className="font-mono">{pending && vals[pending.id]}</span></DialogDescription></DialogHeader>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Original unit DOA, replaced from stock" data-testid="serial-change-note-input" />
          <DialogFooter><Button disabled={!note.trim()} onClick={() => submit(pending, note)} data-testid="serial-change-confirm">Save change</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: project, setData: setProject } = useApi(`/projects/${id}`);
  const [sel, setSel] = useState(null);
  const [docs, setDocs] = useState(new URLSearchParams(window.location.search).get("doc"));
  const [auditKey, setAuditKey] = useState(0);
  if (!project) return null;
  const stages = [...project.stages].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
  const stage = stages.find((s) => s.id === (sel || project.currentStageId)) || stages[stages.length - 1];
  const done = stages.filter((s) => s.isCompleted).length;
  const update = (p) => { setProject(p); setAuditKey((k) => k + 1); };
  const setStatus = async (status) => {
    try { update((await api.patch(`/projects/${id}/status`, { status })).data); toast.success("Status updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <Link to="/projects" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-projects"><ArrowLeft className="h-4 w-4 mr-1" />Projects</Link>
      <PageHeader title={<span className="flex items-center gap-3"><span className="font-mono">{project.projectCode}</span><StatusBadge status={project.status} data-testid="project-status-badge" /></span>}
        subtitle={`${project.customerName} · ${project.customerPhone} · ${project.workflowName}${project.customerVisitAt ? ` · Store visit ${fdt(project.customerVisitAt)}` : ""}${project.amcPlan ? ` · GN Privilege ${project.amcPlan}` : ""}`}>
        {can(user, "projects:status") && <Select value={project.status} onValueChange={setStatus}>
          <SelectTrigger className="w-36" data-testid="project-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["ACTIVE", "ON_HOLD", "COMPLETED", "CANCELLED"].map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
        </Select>}
        {can(user, "projects:status") && <ProjectEditDialog project={project} onSaved={update} />}
        <NotifyDialog project={project} />
        <GalleryButton project={project} canShare={can(user, "projects:gallery")} onShared={() => api.get(`/projects/${id}`).then((r) => update(r.data))} />
        <Button variant="outline" onClick={() => setDocs("jobsheet")} data-testid="print-job-sheet-button"><FileText className="h-4 w-4 mr-2" />Job Sheet</Button>
        <Button variant="outline" onClick={() => nav(`/projects/${project.id}/order-confirmation`)} data-testid="print-receipt-button"><Printer className="h-4 w-4 mr-2" />Print Order Confirmation</Button>
        <TrackingButton project={project} onSaved={update} />
      </PageHeader>
      <BuildSiblings project={project} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[["Order total", inr(project.grandTotal)], ["Advance paid", inr(project.advancePaidAmount)], ["Balance due", inr(project.balanceDueAmount)], ["Progress", `${done}/${stages.length} stages`]].map(([l, v]) => (
          <div key={l} className="rounded-lg border bg-card p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="num font-semibold text-lg mt-1">{v}</div></div>
        ))}
      </div>
      <Tabs defaultValue="workflow">
        <TabsList className="mb-4">
          <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow</TabsTrigger>
          <TabsTrigger value="parts" data-testid="tab-parts">Parts & Serials</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="workflow">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="space-y-1.5" data-testid="stage-stepper">
              {stages.map((s) => {
                const isCur = s.id === project.currentStageId;
                return (
                  <button key={s.id} onClick={() => setSel(s.id)} data-testid={`stage-step-${s.sequenceOrder}`}
                    className={`w-full flex items-center gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${stage.id === s.id ? "border-foreground/30 bg-secondary/60" : "hover:bg-secondary/30"}`}>
                    {s.isCompleted ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className={`h-4 w-4 ${isCur ? "text-primary" : "text-muted-foreground/40"}`} />}
                    <span className="flex-1">{s.stageName}</span>
                    {isCur && <Tone tone="blue">Current</Tone>}
                    {s.requiresPhotos && <Camera className="h-3.5 w-3.5 text-muted-foreground" />}
                  </button>
                );
              })}
            </div>
            <div className="lg:col-span-2"><StagePanel key={stage.id + stage.isCompleted} project={project} stage={stage} setProject={update} /></div>
          </div>
        </TabsContent>
        <TabsContent value="parts"><PartsTab project={project} setProject={update} /></TabsContent>
        <TabsContent value="audit"><AuditList entityType="PROJECT" entityId={project.id} refreshKey={auditKey} /></TabsContent>
      </Tabs>
      <ProjectDocsDialog project={project} open={!!docs} initial={docs} onClose={() => setDocs(null)} />
    </div>
  );
}
